import React, { useEffect } from "react";
import styled from "styled-components";

// 계약서 전문(마크다운) 문서 렌더러 — 전자계약 전문 열람·체결본 열람 공용.
// 종이 문서 메타포라 테마와 무관하게 밝은 지면·명조 서체로 고정한다 (서버 PDF 체결본과 같은 조판 언어).
// 지원 문법은 계약 템플릿이 쓰는 부분집합(h1/h2/문단/**굵게**/- 목록)만 — 범용 마크다운 렌더러가 아니다.

const SERIF_FONT_LINK_ID = "noto-serif-kr-font";

// Noto Serif KR 웹폰트 1회 주입 — 실패해도 폴백 명조 계열(Nanum Myeongjo/serif)로 렌더링된다.
function ensureSerifFont() {
  if (document.getElementById(SERIF_FONT_LINK_ID)) return;
  const link = document.createElement("link");
  link.id = SERIF_FONT_LINK_ID;
  link.rel = "stylesheet";
  link.href =
    "https://fonts.googleapis.com/css2?family=Noto+Serif+KR:wght@400;600;700&display=swap";
  document.head.appendChild(link);
}

type Block = {
  type: "title" | "article" | "paragraph" | "listItem";
  text: string;
};

// 템플릿 부분집합 파서 — 서명란("계약일자:" 이후)은 분리해 다른 스타일을 입힌다.
function parseContract(source: string): {
  body: Block[];
  signature: Block[];
} {
  const toBlocks = (text: string): Block[] =>
    text
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
      .map((line) => {
        if (line.startsWith("# "))
          return { type: "title" as const, text: line.slice(2) };
        if (line.startsWith("## "))
          return { type: "article" as const, text: line.slice(3) };
        if (line.startsWith("- "))
          return { type: "listItem" as const, text: line.slice(2) };
        return { type: "paragraph" as const, text: line };
      });

  const signatureStart = source.indexOf("계약일자:");
  if (signatureStart < 0) return { body: toBlocks(source), signature: [] };
  return {
    body: toBlocks(source.slice(0, signatureStart)),
    signature: toBlocks(source.slice(signatureStart)),
  };
}

// **굵게** 인라인 강조
function renderInline(text: string): React.ReactNode[] {
  return text
    .split(/(\*\*.+?\*\*)/g)
    .map((part, index) =>
      part.startsWith("**") && part.endsWith("**") ? (
        <strong key={index}>{part.slice(2, -2)}</strong>
      ) : (
        <React.Fragment key={index}>{part}</React.Fragment>
      )
    );
}

function renderBlocks(blocks: Block[]): React.ReactNode {
  return blocks.map((block, index) => {
    switch (block.type) {
      case "title":
        return <h1 key={index}>{renderInline(block.text)}</h1>;
      case "article":
        return <h2 key={index}>{renderInline(block.text)}</h2>;
      case "listItem":
        return (
          <div className="row" key={index}>
            {renderInline(block.text)}
          </div>
        );
      default:
        return <p key={index}>{renderInline(block.text)}</p>;
    }
  });
}

export default function ContractDocument({ source }: { source: string }) {
  useEffect(() => {
    ensureSerifFont();
  }, []);

  const { body, signature } = parseContract(source);
  return (
    <Doc>
      {renderBlocks(body)}
      {signature.length > 0 && (
        <div className="signature">{renderBlocks(signature)}</div>
      )}
    </Doc>
  );
}

const Doc = styled.div`
  font-family: "Noto Serif KR", "Nanum Myeongjo", "AppleMyungjo", serif;
  font-size: 14px;
  line-height: 1.95;
  color: #211f1a;
  word-break: keep-all;
  overflow-wrap: anywhere;

  h1 {
    margin: 6px 0 24px;
    padding-bottom: 18px;
    border-bottom: 1.5px solid #211f1a;
    font-size: 19px;
    font-weight: 700;
    letter-spacing: 4px;
    line-height: 1.5;
    text-align: center;
  }

  h2 {
    margin: 26px 0 8px;
    font-size: 15px;
    font-weight: 700;
  }

  p {
    margin: 8px 0;
    text-align: justify;
  }

  strong {
    font-weight: 700;
  }

  .row {
    padding: 2px 0;
  }

  .signature {
    margin-top: 30px;
    padding-top: 20px;
    border-top: 1px solid #211f1a;
  }

  .signature p {
    margin: 12px 0 4px;
    text-align: left;
  }
`;
